import { FC, ReactNode, useState, useEffect, useLayoutEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePostApi, usePutApi } from '@/hooks/UseApiCalls';
import { getAuth } from '@/api/ApiCalls';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import SessionTimeoutDialog from '@/components/SessionTimeoutDialog/SessionTimeoutDialog';
import User from '@/models/User';
import appInsightsService from '@/helpers/AppInsights';
import { getPseudonymousId } from '@/helpers/pseudonymousId';
import {
  clearLastActivity,
  clearRememberMe,
  isTokenExpired,
  markSessionTimedOut,
  setRememberMe,
  writeLastActivity,
} from '@/helpers/sessionTimeout';
import { CURRENT_USER_STORAGE_KEY, restoreUserFromStorage } from '@/helpers/restoreUserFromStorage';
import { canonicalizeBareNewBasename, markExplicitLogout, redirectClearedSessionToLogin } from '@/helpers/sessionPublicPath';
import AuthContext from './AuthContext';

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(restoreUserFromStorage);
  const queryClient = useQueryClient();

  // לא ב-useState: replace ב-initializer הוא impure תחת StrictMode ולא תמיד
  // משנה את /new. כאן, אחרי שהסשן כבר null, הולכים ל-login פעם אחת למסמך.
  useLayoutEffect(() => {
    if (currentUser) {
      canonicalizeBareNewBasename();
      return;
    }
    redirectClearedSessionToLogin();
  }, [currentUser]);
  const { identifyUser: identifyPostHogUser, resetUser: resetPostHogUser, posthog } = usePostHogIdentification();
  const { identifyUser: identifyGAUser, resetUser: resetGAUser, isInitialized: isGAInitialized } = useGoogleAnalytics();

  useEffect(() => {
    if (currentUser) {
      if (posthog) {
        void identifyPostHogUser(currentUser);
      }
      if (isGAInitialized) {
        void identifyGAUser(currentUser);
      }
    }
  }, [currentUser, identifyPostHogUser, identifyGAUser, posthog, isGAInitialized]);

  // ניתוק (או התחברות מחדש) בלשונית אחת חייב לחול על כל השאר, אחרת נשארת
  // לשונית פתוחה שנראית מחוברת עם סשן שכבר הסתיים.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CURRENT_USER_STORAGE_KEY) return;
      if (!event.newValue) {
        setCurrentUser(null);
        return;
      }
      try {
        setCurrentUser(JSON.parse(event.newValue) as User);
      } catch {
        setCurrentUser(null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const updateUser = (user: User | null) => {
    // Every authed query is cached by URL alone, with no user identity in the key. When the identity
    // changes - a fresh login, a logout, or an admin impersonating a user ("login as user") - the
    // previous user's cached responses must be dropped, or the new user briefly sees the old user's
    // data. For impersonation that was fatal (SPR-1133): the impersonated user got the admin's cached
    // fund list, resolved to a fund they don't own, and a 401 from that fund logged them straight out.
    queryClient.clear();

    setCurrentUser(user);
    if (user) {
      localStorage.setItem(CURRENT_USER_STORAGE_KEY, JSON.stringify(user));
      // התחברות היא פעילות: מכאן מתחילה הספירה מחדש.
      writeLastActivity();
      // HAT-1081: גם ל-Application Insights נשלח המזהה הפסאודונימי ולא המייל.
      void getPseudonymousId(user.email).then((pseudonymousId) => {
        if (pseudonymousId) appInsightsService.setAuthenticatedUserContext(pseudonymousId);
      });

      void identifyPostHogUser(user);
      void identifyGAUser(user);
    } else {
      localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
      clearLastActivity();
      clearRememberMe();
      appInsightsService.clearAuthenticatedUserContext();

      resetPostHogUser();
      resetGAUser();
    }
  };

  /**
   * The same account, with details it has just had edited (HAT-1127) - deliberately not routed
   * through updateUser: that one clears the query cache because the identity changed, and here it
   * has not. Wiping every cached response over a corrected surname would throw away the whole
   * screen's data for nothing.
   */
  const updateCurrentUserDetails = (details: Partial<Pick<User, 'firstName' | 'lastName' | 'phoneNumber'>>) => {
    setCurrentUser((previous) => {
      if (!previous) return previous;

      const updated = { ...previous, ...details };
      localStorage.setItem(CURRENT_USER_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const { mutateAsync: loginMutation } = usePostApi<User, object>('users/login');
  const { mutateAsync: signinMutation } = usePostApi<User, object>('users/register');
  const { mutateAsync: forgotPasswordMutation } = usePutApi<void>('users/forgot-password');
  const { mutateAsync: renewForgotPasswordMutation } = usePutApi<void>('users/renew-forgotten-password');

  const login = async (email: string, password: string, rememberMe = false) => {
    const response = await loginMutation({ email, password });
    if (rememberMe) {
      setRememberMe();
    } else {
      clearRememberMe();
    }
    updateUser(response);
  };

  // Impersonation: the server hands back a token of the target user, which replaces the admin
  // session entirely - coming back means logging in again with the admin account.
  //
  // The app is reloaded instead of swapping the user in React state, because a swap leaves behind
  // everything that was set up once for the admin: page-level singletons (the "דיווח טכני" Sprints
  // widget initializes once per page load and stayed on screen), module state, and admin-only UI
  // that is already mounted. That meant the impersonated session kept showing abilities the user
  // does not have until someone refreshed by hand. Writing the session to storage and booting from
  // scratch guarantees the whole app comes up with the impersonated user's permissions.
  // Deliberately without setCurrentUser: re-rendering the admin-only screen we are standing on
  // under the new identity would just flash it as unauthorized on the way out.
  const loginAs = async (email: string) => {
    const response = await getAuth<User>(`users/login-as?email=${encodeURIComponent(email)}`, {
      Authorization: `Bearer ${currentUser?.token}`,
    });

    clearRememberMe();
    localStorage.setItem(CURRENT_USER_STORAGE_KEY, JSON.stringify(response));
    // ההתחברות עצמה היא פעילות: מכאן מתחילה ספירת חוסר הפעילות מחדש.
    writeLastActivity();
    window.location.assign('/new/');
  };

  const signin = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    privacyConsent: { accepted: boolean; version: string; acceptedAt: string },
  ) => {
    const response = await signinMutation({
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      privacyPolicyAccepted: privacyConsent.accepted,
      privacyPolicyVersion: privacyConsent.version,
      privacyPolicyAcceptedAt: privacyConsent.acceptedAt,
    });
    updateUser(response);
  };

  const forgotPassword = async (email: string) => {
    await forgotPasswordMutation({ email, isNewFrontend: true });
  };

  const renewForgotPassword = async (email: string, newPassword: string, verificationToken: string) => {
    await renewForgotPasswordMutation({ email, newPassword, verificationToken });
  };

  const logout = () => {
    markExplicitLogout();
    updateUser(null);
  };

  /*
   * ניתוק על חוסר פעילות. יושב כאן ולא ב-Layout כדי שיחול על כל האזור המחובר
   * בלי תלות בנתיב, ומופעל רק כשיש משתמש - עמודי התרומה הציבוריים לא מושפעים.
   */
  const handleSessionTimeout = () => {
    markSessionTimedOut();
    updateUser(null);
    redirectClearedSessionToLogin();
  };

  const { msUntilLogout, extendSession } = useSessionTimeout({
    enabled: !!currentUser,
    onTimeout: handleSessionTimeout,
  });

  const isUserTokenExpired = () => isTokenExpired(currentUser?.token);

  const isCurrentUserAdmin = (): boolean => {
    return currentUser?.roles?.includes('Admin') ?? false;
  };

  const isCurrentUserDebugger = (): boolean => {
    return currentUser?.roles?.includes('Debugger') ?? false;
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isCurrentUserAdmin,
        isCurrentUserDebugger,
        login,
        loginAs,
        logout,
        signin,
        forgotPassword,
        renewForgotPassword,
        updateCurrentUserDetails,
        isUserTokenExpired,
      }}
    >
      {children}
      {currentUser && msUntilLogout !== null && <SessionTimeoutDialog msUntilLogout={msUntilLogout} onExtend={extendSession} onLogout={handleSessionTimeout} />}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
